import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'

// A stored date is a plain "YYYY-MM-DD". `new Date("2021-01-01")` parses that
// as UTC midnight, which renders as the previous year in negative-offset
// timezones (showing 2020 for a 2021 date). Build the date in local time so the
// year displayed always matches the year stored.
const parseStoredYear = (value) => {
    if (!value) return null;
    const match = /^(\d{4})/.exec(value);
    return match ? new Date(Number(match[1]), 0, 1) : null;
};

export function AddWorkExp({ applicationId }) {
    const [experiences, setExperiences] = useState([]);
    // States for new experience form
    const [company, setCompany] = useState("");
    const [title, setTitle] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [country, setCountry] = useState("");
    const [isRemote, setIsRemote] = useState(false);
    const [startYear, setStartYear] = useState(null);
    const [endYear, setEndYear] = useState(null);
    const [isCurrentJob, setIsCurrentJob] = useState(false);
    const [responsibilities, setResponsibilities] = useState("");
    const showAlert = useAlert();

    // Fetch existing experiences
    useEffect(() => {
        const fetchExperiences = async () => {
            if (!applicationId) return
            try {
                const res = await ApiService.apiExperienceList({ application: applicationId })
                setExperiences(res.results || res)
            } catch (e) {
                showAlert("Failed to load experiences", "error");
            }
        }
        fetchExperiences();
    }, [applicationId, showAlert])

    // POST: Add new experience
    const addExperience = async () => {
        if (experiences.length >= 3) {
            showAlert("Only 3 experiences can be added", "error");
            return;
        }
        const newExp = {
            company,
            title,
            city,
            state,
            country,
            is_remote: isRemote,
            start_date: startYear ? `${startYear.getFullYear()}-01-01` : null,
            end_date: isCurrentJob ? null : (endYear ? `${endYear.getFullYear()}-01-01` : null),
            responsibilities,
            application: applicationId,
        }
        try {
            const res = await ApiService.apiExperienceCreate({ requestBody: newExp })
            setExperiences([...experiences, res])
            // Reset form
            setCompany(""); setTitle(""); setCity(""); setState(""); setCountry(""); setIsRemote(false);
            setStartYear(null); setEndYear(null); setResponsibilities(""); setIsCurrentJob(false);
        } catch (error) {
            showAlert("Failed to add experiences", "error");
            console.log("Failed to add experiences", error);
        }
    }

    // PATCH: Update experience
    const handleFieldChange = (id, field, value) => {
        setExperiences(
            experiences.map((exp) =>
                exp.id === id ? { ...exp, [field]: value } : exp
            )
        );
    };

    const handleCurrentJobChange = (id, checked) => {
        setExperiences(
            experiences.map((exp) =>
                exp.id === id ? { ...exp, is_current: checked, end_date: checked ? null : exp.end_date } : exp
            )
        );
    };

    const updateExperience = async (id) => {
        const updatedExp = experiences.find((exp) => exp.id === id);
        if (!updatedExp) return;

        try {
            // The 'is_current' field is frontend-only and should not be sent to the backend.
            await ApiService.apiExperiencePartialUpdate({
                id,
                requestBody: updatedExp,
            });
            showAlert("Experience updated successfully!", "success");
        } catch (e) {
            showAlert("Failed to update experiences", "error");
        }
    }

    // DELETE: Remove experience
    const removeExperience = async (id) => {
        try {
            await ApiService.apiExperienceDestroy({ id })
            setExperiences(experiences.filter((exp) => exp.id !== id))
        } catch (e) {
            showAlert("Failed to delete experiences", "error");
        }
    }

    return (
        <div className="flex flex-col gap-y-4">
            {experiences.map((exp) => (
                <div key={exp.id} className="p-2 flex flex-col gap-2 mb-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex justify-between items-center">
                        <span className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">{exp.title || 'Role'} at {exp.company}</span>
                        <button
                            className="text-gray-400 dark:text-gray-500 flex flex-row gap-x-2 items-center justify-center hover:text-gray-500 dark:hover:text-gray-300 cursor-pointer"
                            onClick={() => removeExperience(exp.id)}
                        >
                            <XMarkIcon className="size-4" />
                            <span className="text-[14px] font-medium">Remove</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Company</label>
                            <input
                                type="text"
                                value={exp.company || ""}
                                onChange={e => handleFieldChange(exp.id, "company", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Title</label>
                            <input
                                type="text"
                                value={exp.title || ""}
                                onChange={e => handleFieldChange(exp.id, "title", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End year</label>
                            <DatePicker
                                selected={parseStoredYear(exp.end_date)}
                                onChange={date => handleFieldChange(exp.id, "end_date", date ? `${date.getFullYear()}-01-01` : null)}
                                showYearPicker
                                dateFormat="yyyy"
                                minDate={new Date(1990, 0, 1)}
                                maxDate={new Date(2030, 11, 31)}
                                disabled={exp.is_current}
                                className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2 disabled:bg-gray-200"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start year</label>
                            <DatePicker
                                selected={parseStoredYear(exp.start_date)}
                                onChange={date => handleFieldChange(exp.id, "start_date", date ? `${date.getFullYear()}-01-01` : null)}
                                showYearPicker
                                dateFormat="yyyy"
                                minDate={new Date(1990, 0, 1)}
                                maxDate={new Date()}
                                className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex items-center gap-x-2 pt-5">
                            <input
                                type="checkbox"
                                id={`current-${exp.id}`}
                                checked={exp.is_current || false}
                                onChange={(e) => handleCurrentJobChange(exp.id, e.target.checked)}
                                className="size-4"
                            />
                            <label htmlFor={`current-${exp.id}`} className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">I currently work here</label>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">City</label>
                            <input
                                type="text"
                                value={exp.city || ""}
                                onChange={e => handleFieldChange(exp.id, "city", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">State</label>
                            <input
                                type="text"
                                value={exp.state || ""}
                                onChange={e => handleFieldChange(exp.id, "state", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Country</label>
                            <input
                                type="text"
                                value={exp.country || ""}
                                onChange={e => handleFieldChange(exp.id, "country", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                            />
                        </div>
                    </div>
                    <div className="px-2 flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Description</label>
                        <textarea
                            rows="4"
                            maxLength={1500}
                            value={exp.responsibilities || ""}
                            onChange={e => handleFieldChange(exp.id, "responsibilities", e.target.value)}
                            className="p-2 resize-none border border-gray-300 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg"
                        />
                    </div>
                    <div className='flex justify-end'>
                        <button
                            type="button"
                            className="text-[14px] font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-300 flex items-center gap-1"
                            onClick={() => updateExperience(exp.id)}
                        >
                            Update
                        </button>
                    </div>
                </div>
            ))}
            {experiences.length < 3 ? (
                <>
                    <div className="p-4 border border-gray-200 rounded-lg flex flex-col gap-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Company</label>
                                <input type="text" value={company} onChange={e => setCompany(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                            </div>
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Title</label>
                                <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start year</label>
                                <DatePicker
                                    selected={startYear}
                                    onChange={date => setStartYear(date)}
                                    showYearPicker
                                    dateFormat="yyyy"
                                    minDate={new Date(1990, 0, 1)}
                                    maxDate={new Date()}
                                    className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                                />
                            </div>
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End year</label>
                                <DatePicker
                                    selected={endYear}
                                    onChange={date => setEndYear(date)}
                                    showYearPicker
                                    dateFormat="yyyy"
                                    minDate={startYear || new Date(1990, 0, 1)}
                                    maxDate={new Date()}
                                    disabled={isCurrentJob}
                                    className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2 disabled:bg-gray-200"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-x-2">
                            <input id="isCurrent" type="checkbox" checked={isCurrentJob} onChange={e => setIsCurrentJob(e.target.checked)} className="size-4" />
                            <label htmlFor="isCurrent" className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">I currently work here</label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">City</label>
                                <input type="text" value={city} onChange={e => setCity(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                            </div>
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">State</label>
                                <input type="text" value={state} onChange={e => setState(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                            </div>
                            <div className="flex flex-col gap-y-2">
                                <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Country</label>
                                <input type="text" value={country} onChange={e => setCountry(e.target.value)} className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2" />
                            </div>
                        </div>
                        <div className="flex flex-row gap-x-2 items-center">
                            <input id="isRemote" type="checkbox" checked={isRemote} onChange={e => setIsRemote(e.target.checked)} className="size-4" />
                            <label htmlFor="isRemote" className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">This is a remote role</label>
                        </div>
                    </div>
                    <div className="px-2 flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Description</label>
                        <textarea
                            rows="4"
                            maxLength={1500}
                            value={responsibilities}
                            onChange={e => setResponsibilities(e.target.value)}
                            className="p-2 resize-none border border-gray-300 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg"
                        />
                    </div>
                </>
            ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">You can only add up to 3 experience entries.</div>
            )}
            <div className='flex justify-end'>
                <button
                    type="button"
                    onClick={addExperience}
                    className={`text-[14px] font-semibold ${experiences.length >= 3 ? "cursor-none text-blue-300 dark:text-blue-800" : "text-blue-700 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"} flex items-center gap-1`}
                    disabled={experiences.length >= 3}
                >
                    Add Experience
                </button>
            </div>
        </div>
    );
}